/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import PropTypes from 'prop-types';

import {
  Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Badge, Box,
  Code, Divider, Heading, SimpleGrid, Spinner, Stack, Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {CONTENTFUL_METADATA} from '../../contentful/metadata';
import {Component} from './component';

/**
 * Convert field name to Title Case
 * @param {string} fieldName - Field name in camelCase
 * @returns {string} Field name in Title Case
 */
const toTitleCase = (fieldName) => {
  return fieldName
    // Insert space before capital letters
    .replace(/([A-Z])/g, ' $1')
    // Handle acronyms and multiple capitals
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // Capitalize first letter of each word
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Categorize field value types
 * @param {string} fieldName - Field name
 * @param {*} value - Field value
 * @returns {string} Field category
 */
const categorizeField = (fieldName, value) => {
  // System fields
  if (['sys', 'contentfulMetadata', '_id', 'linkedFrom'].includes(fieldName)) {
    return 'system';
  }

  // Skip null/undefined
  if (value === null || value === undefined) {
    return 'skip';
  }

  // Collections (multiple references)
  if (fieldName.endsWith('Collection') && value?.items) {
    return 'collection';
  }

  // Single reference
  if (value && typeof value === 'object' && value.__typename) {
    return 'reference';
  }

  // Complex scalars (objects, arrays)
  if (typeof value === 'object') {
    return 'scalar-complex';
  }

  // Simple scalars
  return 'scalar-simple';
};

/**
 * Render a simple scalar field
 */
const ScalarSimpleField = ({fieldName, value}) => (
  <Box>
    <Text
      color="gray.500"
      fontSize="xs"
      fontWeight="medium"
      letterSpacing="wide"
      mb={1}
      textTransform="uppercase"
    >
      {toTitleCase(fieldName)}
    </Text>
    <Text color="gray.800" fontSize="sm">
      {typeof value === 'boolean' ? (
        <Badge colorScheme={value ? 'green' : 'red'} size="sm">
          {String(value)}
        </Badge>
      ) : (
        value
      )}
    </Text>
  </Box>
);

ScalarSimpleField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  value: PropTypes.any,
};

/**
 * Render a complex scalar field (object/array)
 */
const ScalarComplexField = ({fieldName, value}) => (
  <Box>
    <Text
      color="gray.500"
      fontSize="xs"
      fontWeight="medium"
      letterSpacing="wide"
      mb={2}
      textTransform="uppercase"
    >
      {toTitleCase(fieldName)}
    </Text>
    <Box
      bg="gray.50"
      borderColor="gray.200"
      borderRadius="md"
      borderWidth="1px"
      maxH="200px"
      overflow="auto"
      p={3}
    >
      <Code
        bg="transparent"
        display="block"
        fontSize="sm"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        {JSON.stringify(value, null, 2)}
      </Code>
    </Box>
  </Box>
);

ScalarComplexField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  value: PropTypes.any,
};

/**
 * Render a single reference field
 */
const ReferenceField = ({fieldName, value}) => (
  <Box>
    <Text color="blue.600" fontSize="sm" fontWeight="semibold" mb={2}>
      {toTitleCase(fieldName)}
      <Badge colorScheme="blue" ml={2} size="sm">
        {value.__typename}
      </Badge>
    </Text>
    <Box borderColor="blue.100" borderLeftWidth="3px" pl={3}>
      <Component component={value} />
    </Box>
  </Box>
);

ReferenceField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  value: PropTypes.object.isRequired,
};

/**
 * Render a collection field (multiple references)
 */
const CollectionField = ({fieldName, items}) => (
  <Box>
    <Text color="purple.600" fontSize="sm" fontWeight="semibold" mb={3}>
      {toTitleCase(fieldName)}
      <Badge colorScheme="purple" ml={2} size="sm">
        {items.length} {items.length === 1 ? 'item' : 'items'}
      </Badge>
    </Text>
    <Stack borderColor="purple.100" borderLeftWidth="3px" pl={3} spacing={3}>
      {items.map((item, index) => (
        <Box key={item.sys?.id}>
          <Text color="purple.500" fontSize="xs" fontWeight="medium" mb={1}>
            #{index + 1} · {item.__typename}
          </Text>
          <Component component={item} />
        </Box>
      ))}
    </Stack>
  </Box>
);

CollectionField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
};

/**
 * NotImplemented component - Dynamic renderer for Contentful components
 * Automatically renders all fields based on type metadata.
 *
 * @param {object} props
 * @param {object} props.component - Contentful component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const NotImplemented = ({component, isComplete = true, isFetching = false, ...props}) => {
  if (!component) {
    return null;
  }

  const {__typename} = component;
  const typeMetadata = CONTENTFUL_METADATA[__typename];
  const fields = typeMetadata?.fields || [];

  // Categorize all fields
  const categorized = {
    scalarSimple: [],
    scalarComplex: [],
    reference: [],
    collection: [],
  };

  fields.forEach((fieldName) => {
    const value = component[fieldName];
    const category = categorizeField(fieldName, value);

    switch (category) {
      case 'scalar-simple':
        categorized.scalarSimple.push({fieldName, value});
        break;
      case 'scalar-complex':
        categorized.scalarComplex.push({fieldName, value});
        break;
      case 'reference':
        categorized.reference.push({fieldName, value});
        break;
      case 'collection':
        categorized.collection.push({fieldName, value: value.items});
        break;
      default:
        break;
    }
  });

  const hasContent = Object.values(categorized).some((cat) => cat.length > 0);

  if (!hasContent) {
    return (
      <Box
        bg="gray.50"
        borderColor="gray.200"
        borderRadius="md"
        borderWidth="1px"
        p={6}
        textAlign="center"
        {...props}
      >
        <Heading color="gray.400" mb={2} size="sm">
          {__typename}
        </Heading>
        <Text color="gray.500" fontSize="xs">
          No content available
        </Text>
      </Box>
    );
  }

  // Calculate total field count for badge
  const totalFields = categorized.scalarSimple.length +
    categorized.scalarComplex.length +
    categorized.reference.length +
    categorized.collection.length;

  return (
    <Accordion key={component.sys.id} defaultIndex={[0]} allowMultiple {...props}>
      <AccordionItem
        bg="white"
        borderColor="purple.200"
        borderRadius="lg"
        borderWidth="2px"
        overflow="hidden"
        shadow="sm"
      >
        {/* Accordion Header */}
        <AccordionButton
          _expanded={{bg: 'purple.50', borderBottomWidth: '1px', borderBottomColor: 'purple.200'}}
          _hover={{bg: 'purple.50'}}
          bg="purple.50"
          px={5}
          py={3}
        >
          <Box flex="1" textAlign="left">
            <Heading color="purple.700" size="sm">
              {__typename}
              <Badge colorScheme="purple" ml={2} size="sm">
                {totalFields} {totalFields === 1 ? 'field' : 'fields'}
              </Badge>
              {!isComplete && (
                <Badge colorScheme="orange" ml={2} size="sm">
                  Incomplete
                </Badge>
              )}
              {isFetching && (
                <Badge colorScheme="blue" ml={2} size="sm">
                  <Spinner mr={1} size="xs" /> Loading...
                </Badge>
              )}
            </Heading>
          </Box>
          <AccordionIcon color="purple.700" />
        </AccordionButton>

        {/* Accordion Content */}
        <AccordionPanel p={5}>
          <Stack divider={<Divider borderColor="gray.200" />} spacing={6}>
            {/* Simple Scalar Fields */}
            {categorized.scalarSimple.length > 0 && (
              <Box>
                <Heading color="gray.700" mb={3} size="xs">
                  📝 Fields
                  <Badge colorScheme="gray" ml={2} size="sm">
                    {categorized.scalarSimple.length}
                  </Badge>
                </Heading>
                <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacing={4}>
                  {categorized.scalarSimple.map(({fieldName, value}) => (
                    <ScalarSimpleField key={fieldName} fieldName={fieldName} value={value} />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Complex Scalar Fields */}
            {categorized.scalarComplex.length > 0 && (
              <Box>
                <Heading color="gray.700" mb={3} size="xs">
                  🔧 Complex Data
                  <Badge colorScheme="gray" ml={2} size="sm">
                    {categorized.scalarComplex.length}
                  </Badge>
                </Heading>
                <Stack spacing={4}>
                  {categorized.scalarComplex.map(({fieldName, value}) => (
                    <ScalarComplexField key={fieldName} fieldName={fieldName} value={value} />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Single References */}
            {categorized.reference.length > 0 && (
              <Box>
                <Heading color="blue.700" mb={3} size="xs">
                  🔗 References
                  <Badge colorScheme="blue" ml={2} size="sm">
                    {categorized.reference.length}
                  </Badge>
                </Heading>
                <Stack spacing={4}>
                  {categorized.reference.map(({fieldName, value}) => (
                    <ReferenceField key={fieldName} fieldName={fieldName} value={value} />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Collections */}
            {categorized.collection.length > 0 && (
              <Box>
                <Heading color="purple.700" mb={3} size="xs">
                  📦 Collections
                  <Badge colorScheme="purple" ml={2} size="sm">
                    {categorized.collection.length}
                  </Badge>
                </Heading>
                <Stack spacing={5}>
                  {categorized.collection.map(({fieldName, value}) => (
                    <CollectionField key={fieldName} fieldName={fieldName} items={value} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

NotImplemented.displayName = 'NotImplemented';
NotImplemented.propTypes = {
  component: PropTypes.object,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
